import api from "../../Environment";

async function post(action, params = {}) {
  const response = await api.postMethod(action, params);
  return (response && response.data) || { success: false };
}

export async function fetchMediaLibrary(params = {}) {
  const body = await post("studio/library", params);
  if (!body.success) {
    return {
      items: [],
      total: 0,
      error: body.error || body.error_messages || "Unable to load library",
    };
  }
  return body.data || { items: [], total: 0 };
}

export async function fetchSchedulableAssets(params = {}) {
  const body = await post("studio/schedulable", params);
  if (!body.success) return { items: [], total: 0 };
  return body.data || { items: [], total: 0 };
}

export async function transitionWorkflow(params = {}) {
  return post("studio/workflow", params);
}

export async function fetchCreatorDashboard(creatorId) {
  const body = await post("studio/creator/dashboard", { creator_id: creatorId });
  return body;
}

export async function fetchOrganizationDashboard(organizationId) {
  const body = await post("studio/organization/dashboard", {
    organization_id: organizationId,
  });
  return body;
}

export async function fetchProjectDashboard(projectId) {
  const body = await post("studio/project/dashboard", {
    project_id: projectId,
  });
  return body;
}

export async function fetchSellerHome(params) {
  return post("studio/seller/home", params);
}

export async function fetchSellerProducts(params) {
  return post("studio/seller/products", params);
}

export async function fetchSellerOrders(params) {
  return post("studio/seller/orders", params);
}

export async function fetchSellerInsights(params) {
  return post("studio/seller/insights", params);
}

export async function fetchSellerFinancial(params) {
  return post("studio/seller/financial", params);
}

export async function fetchSellerCatalog(params) {
  return post("studio/seller/catalog", params);
}

export async function saveSellerProduct(params) {
  return post("studio/seller/product/save", params);
}

export async function saveOrganizationProfile(organizationId, fields) {
  return post(
    "studio/organization/profile/save",
    Object.assign({ organization_id: organizationId }, fields || {})
  );
}

/** Authenticated creator donation_mode save (canonical EntityDonationAdapter). */
export async function saveCreatorDonationMode(creatorId, fields = {}) {
  return post(
    "studio/creator/donation/save",
    Object.assign({ creator_id: creatorId }, fields || {})
  );
}

/** @deprecated Prefer entity-specific studio saves; kept for Phase B tooling. */
export async function saveEntityDonationMode(params = {}) {
  if (params.target_type === "creator") {
    return saveCreatorDonationMode(params.target_id, {
      donation_mode: params.mode || "internal",
      donation_url: params.donation_url || params.external_url || "",
    });
  }
  if (params.target_type === "organization") {
    return saveOrganizationProfile(params.target_id, {
      donation_mode: params.mode || "internal",
      donation_url: params.donation_url || params.external_url || "",
    });
  }
  if (params.target_type === "project") {
    return saveProjectFunding({
      project_id: params.target_id,
      donation_mode: params.mode || "internal",
      donation_url: params.donation_url || params.external_url || "",
    });
  }
  return post("donations/save-mode", {
    target_type: params.target_type,
    target_id: params.target_id,
    mode: params.mode || "internal",
    donation_url: params.donation_url || params.external_url || "",
    external_url: params.external_url || params.donation_url || "",
    settings: params.settings || {},
  });
}

export async function saveProjectProfile(params = {}) {
  return post("studio/project/profile/save", params);
}

export async function saveProjectFunding(params = {}) {
  return post("studio/project/funding/save", params);
}

export async function saveProjectMilestone(params = {}) {
  return post("studio/project/milestone/save", params);
}

export async function deleteProjectMilestone(params = {}) {
  return post("studio/project/milestone/delete", params);
}

export async function completeProjectMilestone(params = {}) {
  return post("studio/project/milestone/complete", params);
}

export async function activateNextProjectMilestone(params = {}) {
  return post("studio/project/milestone/activate-next", params);
}

export async function recordProjectDonation(params = {}) {
  return post("studio/project/donation/record", params);
}

export async function saveProjectUpdate(params = {}) {
  return post("studio/project/update/save", params);
}

export async function deleteProjectUpdate(params = {}) {
  return post("studio/project/update/delete", params);
}

export async function saveOrgMember(params = {}) {
  return post("studio/organization/members/save", params);
}

export async function removeOrgMember(params = {}) {
  return post("studio/organization/members/remove", params);
}

export async function fetchComments(adminVideoId) {
  const body = await post("studio/comments", { admin_video_id: adminVideoId });
  if (!body.success) return [];
  return (body.data && body.data.comments) || [];
}

export async function addComment(params = {}) {
  return post("studio/comments", params);
}

export async function fetchActivity(adminVideoId) {
  const body = await post("studio/activity", { admin_video_id: adminVideoId });
  if (!body.success) return [];
  return (body.data && body.data.activity) || [];
}

export async function fetchStudioNotifications(params = {}) {
  const body = await post("studio/notifications", params);
  if (!body.success) return [];
  return (body.data && body.data.notifications) || [];
}

export async function linkChannelAsset(params = {}) {
  return post("studio/channel-link", params);
}

export async function attachToSchedule(params = {}) {
  return post("studio/attach-schedule", params);
}

export async function fetchStudioAnalytics(params = {}) {
  const body = await post("studio/analytics", params);
  if (!body.success) return null;
  return body.data;
}

export async function runContentAutomation(params = {}) {
  return post("studio/automate", params);
}

export async function saveStudioSearch(params = {}) {
  return post("studio/searches/save", params);
}

export async function fetchSavedSearches(params = {}) {
  const body = await post("studio/searches", params);
  if (!body.success) return [];
  return (body.data && body.data.searches) || [];
}

export async function bulkWorkflowTransition(params = {}) {
  return post("studio/workflow/bulk", params);
}

export async function findDuplicateAssets(params = {}) {
  const body = await post("studio/duplicates", params);
  if (!body.success) return [];
  return (body.data && body.data.duplicate_groups) || [];
}

export async function fetchAssetVersions(adminVideoId) {
  const body = await post("studio/versions", { admin_video_id: adminVideoId });
  if (!body.success) return [];
  return (body.data && body.data.versions) || [];
}

export const WORKFLOW_STATUSES = [
  "draft",
  "in_review",
  "changes_requested",
  "approved",
  "scheduled",
  "published",
  "expired",
  "archived",
];
